// POS payment settings resolvers (web).
//
// Mirrors admin-mobile-app/src/utils/posPayments.js. The two are kept byte-identical in
// LOGIC (only the header line and export style differ), following the existing
// money.js / partyLabel.js / storeTicket.js / tenantTime.js duplication convention, so
// the web POS queue and the mobile POS queue cannot disagree about when the "Charge
// card" button applies to an order.
//
// Pure functions that read tenant payment settings off the POS-queue response block.
// No React, no aws-amplify import, so they stay unit-testable without rendering the
// queue (the queue page pulls in CSS + amplify, which the jest transform can't load).

/**
 * inAppCardPaymentEnabled(settings) -> boolean
 *
 * Whether this tenant has opted into in-app card payment (Stripe: card + Apple/Google
 * Pay collected inside the admin, instead of the hosted-checkout redirect). It gates the
 * "Charge card" button, and ONLY for Stripe tenants; the provider check lives at the
 * call site, not here.
 *
 * FAIL-CLOSED, matching partyLabel.tablePartiesEnabled: an absent key, null, malformed
 * settings, a string "false"/"true", or a not-yet-loaded queue response all resolve to
 * false. Only an explicit boolean `true` opts a tenant in. The direction matters --
 * resolving a not-yet-loaded gate to true would flash a card button at tenants that
 * never asked for it, and (worse, for a payment control) on the wrong provider.
 */
export function inAppCardPaymentEnabled(settings) {
  if (!settings || typeof settings !== 'object') return false;
  return settings.inAppCardPaymentEnabled === true;
}

/**
 * canChargeCardInApp(order, settings) -> boolean
 *
 * Whether the in-app "Charge card" action applies to THIS order. Pure and fail-closed
 * so it is unit-testable without Stripe.js, and so the button never appears where it
 * must not.
 *
 * SCOPE: EVERY UNPAID ORDER ON A STRIPE TENANT, INCLUDING IN-STORE / STAFF TICKETS.
 * True requires ALL of:
 *   - the tenant admin has enabled in-app card payment (inAppCardPaymentEnabled), AND
 *   - Stripe is the effective provider (see below), AND
 *   - the order is unpaid (payment_status === 'pending_payment'), AND
 *   - the order is not cancelled.
 *
 * EFFECTIVE PROVIDER -- why this is not just `order.posProvider`. An in-store ticket
 * created in the admin ("Take Order" / staff order) carries NO `posProvider` at all:
 * `pos_order_core.create_pos_order` stamps it only `if not is_staff_order`. Requiring
 * the field on the order therefore hid the button on exactly the tickets a cashier
 * collects at the counter. So the order's provider WINS WHEN PRESENT (a Square /
 * Clover / Razorpay / PhonePe order is still excluded -- in-app card is Stripe-only),
 * and when the order carries none we fall back to the TENANT's configured provider
 * (`settings.posProvider`). A non-Stripe tenant is unaffected either way.
 *
 * NO paymentMode CONDITION. `pending_payment` already excludes everything that has been
 * collected, which is the only thing the old PAY_AT_POS check was really protecting
 * against (a PAY_NOW order that settled on the hosted page arrives `paid` and is
 * filtered out here). An unpaid PAY_NOW order is a customer who abandoned the hosted
 * checkout and is now standing at the counter -- that genuinely needs collecting, so
 * excluding it was wrong.
 *
 * Any missing/malformed field resolves to false. This does NOT replace the server guards
 * in _handle_stripe_payment_intent -- it only decides whether to show the button.
 */
export function canChargeCardInApp(order, settings) {
  if (!inAppCardPaymentEnabled(settings)) return false;
  return _isCollectableStripeOrder(order, settings);
}

/**
 * razorpayUpiAtStoreEnabled(settings) -> boolean
 *
 * Whether this tenant opted into collecting UPI at the counter via a Razorpay dynamic QR
 * (razorpay-upi-dinein-qr spec). Sibling of inAppCardPaymentEnabled / terminalReaderEnabled
 * -- an independent toggle. FAIL-CLOSED: only an explicit boolean `true` opts in. Byte-
 * identical in LOGIC to the admin-mobile-app copy, so the two surfaces cannot disagree
 * about which tenant may collect UPI.
 */
export function razorpayUpiAtStoreEnabled(settings) {
  if (!settings || typeof settings !== 'object') return false;
  return settings.razorpayUpiAtStoreEnabled === true;
}

/**
 * canCollectUpiAtStore(order, settings) -> boolean
 *
 * Whether the "Collect UPI" (Razorpay dynamic QR) action applies to THIS order. Pure and
 * fail-closed so it is unit-testable and the button never appears where it must not.
 *
 * True requires ALL of:
 *   - the tenant admin enabled razorpayUpiAtStoreEnabled, AND
 *   - RAZORPAY is the effective provider (order.posProvider wins when present; a staff
 *     IN-STORE ticket carries none, so fall back to the tenant's configured provider --
 *     the same effective-provider rule as the Stripe gate), AND
 *   - the order currency is INR (UPI settles only INR; mirrors the backend guard), AND
 *   - the order is unpaid (payment_status === 'pending_payment'), AND
 *   - the order is not cancelled.
 *
 * NOTE: this does NOT reuse _isCollectableStripeOrder, which hard-requires STRIPE and
 * excludes RAZORPAY by design. The provider requirement is the opposite here, so the
 * eligibility is written in parallel rather than shared.
 *
 * Any missing/malformed field resolves to false. Does NOT replace the server guards in
 * the QR handlers -- it only decides whether to show the button.
 */
export function canCollectUpiAtStore(order, settings) {
  if (!razorpayUpiAtStoreEnabled(settings)) return false;
  if (!order || typeof order !== 'object') return false;

  const orderProvider = String(order.posProvider || '').trim().toUpperCase();
  const tenantProvider = String((settings && settings.posProvider) || '').trim().toUpperCase();
  const effectiveProvider = orderProvider || tenantProvider;
  if (effectiveProvider !== 'RAZORPAY') return false;

  const currency = String(order.currency || '').trim().toUpperCase();
  if (currency !== 'INR') return false;

  const status = String(order.payment_status || order.paymentStatus || '').trim().toLowerCase();
  if (status !== 'pending_payment') return false;

  const orderStatus = String(order.status || order.order_status || '').trim().toUpperCase();
  if (orderStatus === 'CANCELLED' || orderStatus === 'CANCELED') return false;

  return true;
}

/**
 * scanToPayEnabled(settings) -> boolean
 *
 * Whether this tenant opted into terminal-free Scan-to-Pay (take-order-scan-to-pay spec):
 * staff show a QR of the provider hosted-checkout URL that an in-store customer scans and
 * pays on their own phone. Independent toggle, sibling of inAppCardPaymentEnabled /
 * razorpayUpiAtStoreEnabled. FAIL-CLOSED -- only an explicit boolean `true` opts in, so no
 * "Collect by QR" button flashes before settings load or for a tenant that never asked.
 * Byte-identical in LOGIC to the mobile gate so the two surfaces cannot disagree.
 */
export function scanToPayEnabled(settings) {
  if (!settings || typeof settings !== 'object') return false;
  return settings.scanToPayEnabled === true;
}

/**
 * canCollectScanToPay(order, settings) -> boolean
 *
 * Whether the "Collect by QR" (hosted-checkout Scan-to-Pay) action applies to THIS order.
 * Pure and fail-closed, mirroring the inline mobile gate in admin-mobile-app/App.js.
 *
 * True requires ALL of:
 *   - the tenant admin enabled scanToPayEnabled, AND
 *   - the effective provider is one of STRIPE / SQUARE / CLOVER (US card providers that
 *     offer a hosted checkout; order.posProvider wins when present, else the tenant's
 *     configured provider -- same effective-provider rule as the other gates), AND
 *   - the order is PAY_AT_POS (an in-store ticket to be collected now; a PAY_NOW order is
 *     a hosted-checkout flow of its own), AND
 *   - the order is unpaid (payment_status === 'pending_payment'), AND
 *   - the order is not cancelled.
 *
 * Any missing/malformed field resolves to false. Does NOT replace the server guards in the
 * scan-to-pay handlers -- it only decides whether to show the button. Only STRIPE is wired
 * end-to-end today; SQUARE/CLOVER are allow-listed here for parity but the backend returns
 * 503 until their adapters exist, which is why the button stays additive and pay-optional.
 */
export function canCollectScanToPay(order, settings) {
  if (!scanToPayEnabled(settings)) return false;
  if (!order || typeof order !== 'object') return false;

  const orderProvider = String(order.posProvider || '').trim().toUpperCase();
  const tenantProvider = String((settings && settings.posProvider) || '').trim().toUpperCase();
  const effectiveProvider = orderProvider || tenantProvider;
  if (!['STRIPE', 'SQUARE', 'CLOVER'].includes(effectiveProvider)) return false;

  const paymentMode = String(order.posPaymentMode || order.paymentMode || order.payment_mode || '').trim().toUpperCase();
  if (paymentMode !== 'PAY_AT_POS') return false;

  const status = String(order.payment_status || order.paymentStatus || '').trim().toLowerCase();
  if (status !== 'pending_payment') return false;

  const orderStatus = String(order.status || order.order_status || '').trim().toUpperCase();
  if (orderStatus === 'CANCELLED' || orderStatus === 'CANCELED') return false;

  return true;
}

/**
 * _isCollectableStripeOrder(order, settings) -> boolean  (private)
 *
 * The order-side eligibility shared by BOTH in-app card and Terminal-reader collection:
 * effective provider is STRIPE, order is unpaid (pending_payment), and not cancelled.
 * Extracted so the two public gates cannot drift from each other -- the ONLY difference
 * between them is which tenant toggle they check.
 */
function _isCollectableStripeOrder(order, settings) {
  if (!order || typeof order !== 'object') return false;

  const orderProvider = String(order.posProvider || '').trim().toUpperCase();
  const tenantProvider = String((settings && settings.posProvider) || '').trim().toUpperCase();
  const effectiveProvider = orderProvider || tenantProvider;
  if (effectiveProvider !== 'STRIPE') return false;

  const status = String(order.payment_status || order.paymentStatus || '').trim().toLowerCase();
  if (status !== 'pending_payment') return false;

  const orderStatus = String(order.status || order.order_status || '').trim().toUpperCase();
  if (orderStatus === 'CANCELLED' || orderStatus === 'CANCELED') return false;

  return true;
}

/**
 * terminalReaderEnabled(settings) -> boolean
 *
 * Whether the tenant admin opted into card-present collection via a Stripe Terminal
 * external reader (BBPOS WisePad 3 / Stripe Reader M2). Separate toggle from
 * inAppCardPaymentEnabled: a tenant may enable keyed/in-app card, the reader, both, or
 * neither. FAIL-CLOSED -- only an explicit boolean `true` opts in (pos-terminal-reader spec).
 */
export function terminalReaderEnabled(settings) {
  if (!settings || typeof settings !== 'object') return false;
  return settings.terminalReaderEnabled === true;
}

/**
 * canChargeOnReader(order, settings) -> boolean
 *
 * Whether the "Charge on reader" (Stripe Terminal card-present) action applies to THIS
 * order. Sibling of canChargeCardInApp -- SAME order eligibility (Stripe effective
 * provider + unpaid + not cancelled), gated on the SEPARATE terminalReaderEnabled toggle.
 * Pure and fail-closed. This does NOT replace the server guards in the Terminal
 * intent/capture handlers -- it only decides whether to show the button.
 *
 * NOTE (web): the web POS queue does not render a "Charge on reader" button (a Bluetooth
 * reader pairs to a device, not a browser). This resolver exists for parity + tests.
 */
export function canChargeOnReader(order, settings) {
  if (!terminalReaderEnabled(settings)) return false;
  return _isCollectableStripeOrder(order, settings);
}

/**
 * squareTerminalCompletion(order) -> { label, completeStatus }
 *
 * Resolves the order-type-adaptive label suffix and the fulfillment status to auto-advance
 * to after a SUCCESSFUL "Pay on Square Terminal" charge (square-terminal-checkout).
 *
 * WHY. The customer taps their card when they are ready to complete, so the payment moment
 * IS the completion moment -- pay + complete are one action, not two. This resolver is the
 * single source of truth shared by the button label and the post-payment auto-advance, so
 * the label and the action can never disagree.
 *
 *   - Dine-in  (orderType DINE_IN)        -> "Pay on Square Terminal + Dine-in Complete",
 *                                             completeStatus "DINE_IN_COMPLETE".
 *   - Pickup / to-go / pay-at-store (any  -> "Pay on Square Terminal + Pick-up Complete",
 *     non-delivery, non-dine-in)             completeStatus "PICKUP_COMPLETE".
 *   - Delivery (fulfillment_type delivery) -> "Pay on Square Terminal", completeStatus ""
 *                                             (payment only; delivery is not completed by
 *                                             a counter payment, so no auto-advance).
 *
 * Pure and defensive: an unknown/absent order shape falls through to the pickup label,
 * which is the safe default for a counter payment. `completeStatus` is "" when nothing
 * should auto-advance, which the caller treats as "pay only".
 */
export function squareTerminalCompletion(order) {
  const orderType = String(order?.orderType || order?.customerSubmittedSnapshot?.orderType || '')
    .trim()
    .toUpperCase();
  const isDelivery = String(order?.fulfillment_type || '').trim().toLowerCase() === 'delivery';

  if (isDelivery) {
    return { label: 'Pay on Square Terminal', completeStatus: '' };
  }
  if (orderType === 'DINE_IN') {
    return { label: 'Pay on Square Terminal + Dine-in Complete', completeStatus: 'DINE_IN_COMPLETE' };
  }
  return { label: 'Pay on Square Terminal + Pick-up Complete', completeStatus: 'PICKUP_COMPLETE' };
}
